import React from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import Dropdown from "../Dropdown";
import Icon from "../Icon";
import { AudioClipLine, AudioClipVoice } from "../../../types/types";
import { DropdownOptionOrGroup } from "../../../types/screensTypes";
import { TranscriptCard } from "./utils";
import styles from "./AudioEditorTranscriptFields.module.css";

/** A card as edited in the modal: what is heard in a clip, and who says it. */
export interface TranscriptEntry extends TranscriptCard {
    /** False when the user judged this block is not speech, or no longer matches. */
    kept: boolean;
    /**
     * The user is naming the voice by hand rather than picking one.
     *
     * Held explicitly because it cannot be read back from the voice: choosing "custom
     * name" starts with an empty name, which is indistinguishable from no voice at all.
     * Deriving it is what made the option impossible to select — it snapped straight back.
     */
    custom: boolean;
}

export interface VoiceOption {
    value: string;
    label: string;
    /** Set when the option is a cloned voice: capsules store the clone row id, not this one. */
    voiceCloneDbId?: string | null;
    /** Set when the option is a designed voice, for the same reason. */
    promptVoiceDbId?: string | null;
}

/** A card starts kept: leaving it empty is how the user says "this is not speech". */
export const buildEntries = (cards: TranscriptCard[]): TranscriptEntry[] =>
    cards.map((card) => ({ ...card, kept: true, custom: Boolean(card.voice.voiceLabel) }));

/**
 * What the user validated, grouped by the clip that carries it.
 *
 * This is the only thing the save needs: the composition stores these blocks per clip,
 * and the dialog lines of the audio are regenerated from them. A clip that gets no block
 * is sound design — kept as is by a translation instead of being replaced.
 */
export const linesByClip = (entries: TranscriptEntry[]): Record<string, AudioClipLine[]> =>
    entries
        .filter((entry) => entry.kept && entry.text.trim().length > 0)
        .reduce<Record<string, AudioClipLine[]>>((byClip, entry, order) => {
            const line: AudioClipLine = {
                text: entry.text.trim(),
                // Grouping by clip loses the order the cards were in, and that order is
                // the user's: they can move a block ahead of one carried by an earlier
                // clip. Carried on the block itself so the server can restore it.
                order,
                voiceAIId: entry.voice.voiceAIId ?? null,
                voiceCloneId: entry.voice.voiceCloneId ?? null,
                voicePromptId: entry.voice.voicePromptId ?? null,
                // An empty name is not a name: the user opened the field and left it blank.
                voiceLabel: entry.voice.voiceLabel?.trim() || null,
            };
            byClip[entry.clipId] = [...(byClip[entry.clipId] || []), line];
            return byClip;
        }, {});

/**
 * Clips that arrived with a text and no longer keep any.
 *
 * Emptying a clip is how the user says "this is not speech", and the consequence is real:
 * the clip becomes sound design, so it is never translated and every translation replays
 * it as it is, in its original language. Silent when the clip never had a text — a music
 * bed left empty is simply a music bed.
 */
export const droppedVoiceClips = (entries: TranscriptEntry[]): string[] => {
    const byClip = new Map<string, { name: string; hadText: boolean; keeps: boolean }>();
    entries.forEach((entry) => {
        const current = byClip.get(entry.clipId);
        const keeps = entry.kept && entry.text.trim().length > 0;
        if (current) {
            current.hadText = current.hadText || entry.hadText === true;
            current.keeps = current.keeps || keeps;
        } else {
            byClip.set(entry.clipId, { name: entry.clipName, hadText: entry.hadText === true, keeps });
        }
    });
    return [...byClip.values()].filter((clip) => clip.hadText && !clip.keeps).map((clip) => clip.name);
};

/** True when nothing identifies who speaks this block. */
const hasNoVoice = (voice: AudioClipVoice) =>
    !voice.voiceAIId && !voice.voiceCloneId && !voice.voicePromptId && !voice.voiceLabel;

const CUSTOM = "__custom__";
// Dropdown already treats "__none__" as its empty value and hands back "" for it.
const NONE = "__none__";

/**
 * The option that stands for a voice, when there is one in the list.
 *
 * A capsule generated from a clone or a designed voice stores the id of that row, while
 * the list is keyed by VoiceAI id — so those two need translating before anything can be
 * preselected. Without this, an attributed capsule opens on "no voice", which reads as if
 * the attribution had been lost.
 */
const optionValueFor = (voice: AudioClipVoice, options: VoiceOption[]): string | null => {
    if (voice.voiceAIId) return voice.voiceAIId;
    if (voice.voiceCloneId) {
        return options.find((option) => option.voiceCloneDbId === voice.voiceCloneId)?.value ?? null;
    }
    if (voice.voicePromptId) {
        return options.find((option) => option.promptVoiceDbId === voice.voicePromptId)?.value ?? null;
    }
    return null;
};

/**
 * The voice a picked option stands for, in the column that actually holds it.
 *
 * The list is keyed by the ElevenLabs id for a clone or a designed voice, not by a
 * VoiceAI id. Writing that id as `voiceAIId` would point a foreign key at a row that does
 * not exist — both `AudioAI.voiceAIId` and `AudioAIDialogLine.voiceAIId` are constrained —
 * and would also hide from the translation worker which kind of voice this is.
 */
export const voiceFromOption = (choice: string, options: VoiceOption[]): AudioClipVoice => {
    const option = options.find((candidate) => candidate.value === choice);
    if (option?.voiceCloneDbId) return { voiceCloneId: option.voiceCloneDbId };
    if (option?.promptVoiceDbId) return { voicePromptId: option.promptVoiceDbId };
    return { voiceAIId: choice };
};

interface AudioEditorTranscriptFieldsProps {
    entries: TranscriptEntry[];
    onChange: (entries: TranscriptEntry[]) => void;
    voiceOptions: VoiceOption[];
    disabled?: boolean;
}

/**
 * Lets the user say, for every clip of the mix, what is said in it and by whom.
 *
 * Both answers drive the translation: the text is what gets translated, and the voice is
 * the key the translation screen offers for mapping. A free-text name is a real key too,
 * not just a label — two named voices can be sent to two different target voices.
 */
const AudioEditorTranscriptFields: React.FC<AudioEditorTranscriptFieldsProps> = ({
    entries,
    onChange,
    voiceOptions,
    disabled,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    if (entries.length === 0) return null;

    const update = (id: string, patch: Partial<TranscriptEntry>) =>
        onChange(entries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));

    // Dropdown maps its "__none__" option back to an empty string.
    const setVoice = (id: string, choice: string) => {
        if (choice === "" || choice === NONE) {
            update(id, { voice: {}, custom: false });
            return;
        }
        if (choice === CUSTOM) {
            // Keep whatever was already typed: switching away and back should not erase it.
            const current = entries.find((entry) => entry.id === id);
            update(id, { voice: { voiceLabel: current?.voice.voiceLabel ?? "" }, custom: true });
            return;
        }

        update(id, { voice: voiceFromOption(choice, voiceOptions), custom: false });
    };

    // Timeline order is only a proposal: when the same voice speaks twice, the user is
    // the only one who knows how their text splits across the blocks.
    const move = (index: number, delta: number) => {
        const target = index + delta;
        if (target < 0 || target >= entries.length) return;
        const reordered = [...entries];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        onChange(reordered);
    };

    /**
     * Cut a block in two. Nothing can tell where a text should be split, so the second
     * card starts empty and the user moves the relevant half across. It stays on the same
     * clip and keeps its voice — both halves are the same passage.
     */
    const split = (index: number) => {
        const source = entries[index];
        const next = [...entries];
        next.splice(index + 1, 0, {
            id: uuidv4(),
            clipId: source.clipId,
            clipName: source.clipName,
            text: "",
            voice: { ...source.voice },
            kept: true,
            custom: source.custom,
        });
        onChange(next);
    };

    const formatStart = (seconds: number) => {
        const total = Math.max(0, Math.round(seconds));
        return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
    };

    const spoken = entries.filter((entry) => entry.kept && entry.text.trim().length > 0);
    const unnamed = spoken.filter((entry) => hasNoVoice(entry.voice));
    const dropped = droppedVoiceClips(entries);

    return (
        <div className={styles.block}>
            <div className={styles.blockHead}>
                <Icon name="ic-file-text" />
                {t("transcriptTitle")}
            </div>
            <p className={styles.hint}>{t("transcriptHintClips")}</p>

            <div className={styles.list}>
                {entries.map((entry, index) => {
                    // Same order as the worker: a real voice wins, the free-text name is
                    // only what stands in when there is none.
                    const matched = optionValueFor(entry.voice, voiceOptions);
                    const known = matched !== null && voiceOptions.some((option) => option.value === matched);

                    // A voice deleted or deactivated since would otherwise fall back to
                    // "no voice", which reads as a lost attribution rather than a voice
                    // that is no longer offered. Only decided once the list has loaded.
                    const missingVoice = !entry.custom && voiceOptions.length > 0 && matched !== null && !known;
                    const missingValue = "__missing__";

                    const selected = entry.custom
                        ? CUSTOM
                        : known
                          ? (matched as string)
                          : missingVoice
                            ? missingValue
                            : NONE;

                    const options: DropdownOptionOrGroup[] = [
                        { value: NONE, label: t("transcriptVoiceNone") },
                        { value: CUSTOM, label: t("transcriptVoiceCustom") },
                        ...(missingVoice
                            ? [{ value: missingValue, label: t("transcriptVoiceUnavailable"), isDisabled: true }]
                            : []),
                        ...(voiceOptions.length > 0
                            ? [
                                  {
                                      label: t("transcriptVoiceLibrary"),
                                      options: voiceOptions.map((option) => ({
                                          value: option.value,
                                          label: option.label,
                                      })),
                                  },
                              ]
                            : []),
                    ];

                    return (
                        <div key={entry.id} className={`${styles.card} ${entry.kept ? "" : styles.cardDropped}`.trim()}>
                            <div className={styles.cardHead}>
                                <span className={styles.cardName}>{entry.clipName}</span>
                                {entry.startTime !== undefined && (
                                    <span className={styles.cardTime}>{formatStart(entry.startTime)}</span>
                                )}
                                {entries.length > 1 && (
                                    <span className={styles.cardMove}>
                                        <button
                                            type="button"
                                            className={styles.cardAction}
                                            title={t("transcriptMoveUp") || ""}
                                            onClick={() => move(index, -1)}
                                            disabled={disabled || index === 0}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.cardAction}
                                            title={t("transcriptMoveDown") || ""}
                                            onClick={() => move(index, 1)}
                                            disabled={disabled || index === entries.length - 1}
                                        >
                                            ↓
                                        </button>
                                    </span>
                                )}
                                {entry.kept && (
                                    <button
                                        type="button"
                                        className={styles.cardAction}
                                        title={t("transcriptSplitTooltip") || ""}
                                        onClick={() => split(index)}
                                        disabled={disabled}
                                    >
                                        {t("transcriptSplit")}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={`${styles.cardAction} ${entry.kept ? styles.cardActionDanger : ""}`.trim()}
                                    onClick={() => update(entry.id, { kept: !entry.kept })}
                                    disabled={disabled}
                                >
                                    {entry.kept ? t("transcriptDrop") : t("transcriptRestore")}
                                </button>
                            </div>

                            {entry.kept ? (
                                <>
                                    <textarea
                                        className={styles.body}
                                        value={entry.text}
                                        placeholder={t("transcriptPlaceholder")}
                                        onChange={(e) => update(entry.id, { text: e.target.value })}
                                        disabled={disabled}
                                    />
                                    <div className={styles.voiceRow}>
                                        <span className={styles.voiceLabel}>{t("transcriptVoice")}</span>
                                        <div className={styles.voiceSelect}>
                                            <Dropdown
                                                label=""
                                                options={options}
                                                onChange={(choice) => setVoice(entry.id, choice)}
                                                loading={false}
                                                error={false}
                                                disabled={disabled}
                                                value={selected}
                                                placeholder={t("transcriptVoiceNone")}
                                                fullWidth
                                                isWithoutMargin
                                            />
                                        </div>
                                        {entry.custom && (
                                            <input
                                                className={styles.voiceCustom}
                                                value={entry.voice.voiceLabel || ""}
                                                placeholder={t("transcriptVoiceCustomPlaceholder")}
                                                onChange={(e) =>
                                                    update(entry.id, {
                                                        voice: { voiceLabel: e.target.value },
                                                        custom: true,
                                                    })
                                                }
                                                disabled={disabled}
                                            />
                                        )}
                                    </div>
                                </>
                            ) : (
                                <p className={styles.droppedNote}>{t("transcriptDropped")}</p>
                            )}
                        </div>
                    );
                })}
            </div>

            {spoken.length === 0 && (
                <div className={styles.warning}>
                    <Icon name="ic-alert-triangle" />
                    <span>{t("transcriptEmptyWarning")}</span>
                </div>
            )}

            {dropped.length > 0 && (
                <div className={styles.warning}>
                    <Icon name="ic-alert-triangle" />
                    <span>{t("transcriptWarnDroppedVoice", { names: dropped.join(", ") })}</span>
                </div>
            )}

            {spoken.length > 0 && unnamed.length > 0 && (
                <div className={styles.warning}>
                    <Icon name="ic-alert-triangle" />
                    <span>
                        {t("transcriptWarnNoVoice", { names: unnamed.map((entry) => entry.clipName).join(", ") })}
                    </span>
                </div>
            )}
        </div>
    );
};

export default AudioEditorTranscriptFields;
